import { Flex, Loader, Table, Text, Title } from '@mantine/core';
import { useMemo } from 'react';
import { PageWrapper } from '../../components/PageWrapper';
import { useConstants } from '../../hooks/use-constants';
import type { DocCategory, DocSection } from '../../types/constants';
import './vegetation.css';

const stateLookup: Record<string, string> = {
  'Alice Springs': 'Northern Territory',
  'Arnhem-Roper': 'Northern Territory',
  'Central Highlands/Northern': 'Queensland',
  'Central North/Midlands/South East': 'Tasmania',
  'Central Plateau/Derwent Valley': 'Tasmania',
  'Central West': 'WA',
  'Central West/Flinders': 'Queensland',
  'Central Wheat Belt': 'WA',
  'Channel Country': 'Queensland',
  'Darling Downs/Burnett': 'Queensland',
  'Darwin-Daly': 'Northern Territory',
  'East Coast': 'Tasmania',
  'East Gippsland': 'Victoria',
  'Goldfields/Eucla': 'WA',
  'Maranoa/Warrego': 'Queensland',
  'Mid-North/Flinders': 'SA',
  'North Coast': 'NSW',
  'North East Vic': 'Victoria',
  'North East': 'Tasmania',
  'North West': 'Tasmania',
  'North West/Gulf': 'Queensland',
  'Northern Country': 'Victoria',
  'Northern Tablelands': 'NSW',
  'Northern Wheat/Sheep': 'NSW',
  'South Coast': 'NSW',
  'South Coastal': 'WA',
  'South East': 'SA',
  'South West Vic': 'Victoria',
  'South West': 'WA',
  'Southern Tablelands': 'NSW',
  'Southern Wheat/Sheep': 'NSW',
  'Victoria River-TennantCreek': 'Northern Territory',
  'West Coast/Eyre': 'SA',
  'West/South Coast': 'Tasmania',
  'West/South Gippsland': 'Victoria',
  Central: 'Victoria',
  Gascoyne: 'WA',
  Interior: 'WA',
  Kimberley: 'WA',
  Mallee: 'Victoria',
  Murray: 'SA',
  Pastoral: 'SA',
  Pilbara: 'WA',
  Western: 'WA',
  Wimmera: 'Victoria',
};

function TreeSpeciesRow({ title, species }: { title: string; species: string }) {
  if (species === 'No tree data available') {
    return null;
  }
  return (
    <Table.Tr>
      <Table.Th>{title}</Table.Th>
      <Table.Td>{species}</Table.Td>
    </Table.Tr>
  );
}

function VegetationDocs({ treeRegions }: { treeRegions: DocSection }) {
  const regions: Record<string, string> = useMemo(() => ({}), []);
  const soilType1: Record<string, string> = useMemo(() => ({}), []);
  const soilType2: Record<string, string> = useMemo(() => ({}), []);
  const treeSpecies1: Record<string, string> = useMemo(() => ({}), []);
  const treeSpecies2: Record<string, string> = useMemo(() => ({}), []);
  const treeSpecies3: Record<string, string> = useMemo(() => ({}), []);
  const treeSpecies4: Record<string, string> = useMemo(() => ({}), []);
  const treeSpecies5: Record<string, string> = useMemo(() => ({}), []);
  const treeSpecies6: Record<string, string> = useMemo(() => ({}), []);

  useMemo(() => {
    treeRegions.values.forEach((v) => {
      if (typeof v.name === 'string' && (typeof v.value === 'number' || typeof v.value === 'string')) {
        const name = v.name.toString();
        const value = v.value.toString();
        const [_0, _1, type] = v.path;
        if (type === 'RegionNo') {
          regions[name] = value;
        }
        if (v.path[2] === 'SoilType1') {
          soilType1[name] = value;
        }
        if (v.path[2] === 'SoilType2') {
          soilType2[name] = value;
        }
        if (v.path[2] === 'TreeSpecies1') {
          treeSpecies1[name] = value;
        }
        if (v.path[2] === 'TreeSpecies2') {
          treeSpecies2[name] = value;
        }
        if (v.path[2] === 'TreeSpecies3') {
          treeSpecies3[name] = value;
        }
        if (v.path[2] === 'TreeSpecies4') {
          treeSpecies4[name] = value;
        }
        if (v.path[2] === 'TreeSpecies5') {
          treeSpecies5[name] = value;
        }
        if (v.path[2] === 'TreeSpecies6') {
          treeSpecies6[name] = value;
        }
      }
    });
  }, [
    regions,
    soilType1,
    soilType2,
    treeRegions.values,
    treeSpecies1,
    treeSpecies2,
    treeSpecies3,
    treeSpecies4,
    treeSpecies5,
    treeSpecies6,
  ]);

  return (
    <Flex w="100%" direction="column" gap="md">
      <Title order={1} mb="md">
        Vegetation input
      </Title>
      <Text>
        Most calculators allow you to supply a list of vegetation areas, each with a different region, soil type and
        tree species. It is important that you supply soil type and tree species that are valid for the region. This
        page helps you to find possible correct values for each region. Once you have found your region, you can choose
        one of the soil types and tree species to use for the calculator.
      </Text>
      <Table>
        <Table.Tbody>
          {Object.keys(regions)
            .sort((a, b) => a.localeCompare(b))
            .map((regionName) => (
              <>
                <Table.Tr className="region-row">
                  <Table.Th>Region</Table.Th>
                  <Table.Td>
                    <Text fw={700}>{regionName}</Text>
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>State</Table.Th>
                  <Table.Td>{stateLookup[regionName] ?? '-'}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>Soil Type 1</Table.Th>
                  <Table.Td>{soilType1[regionName]}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>Soil Type 2</Table.Th>
                  <Table.Td>{soilType2[regionName]}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Th>Tree Species 1</Table.Th>
                  <Table.Td>{treeSpecies1[regionName]}</Table.Td>
                </Table.Tr>
                <TreeSpeciesRow title="Tree Species 2" species={treeSpecies2[regionName]} />
                <TreeSpeciesRow title="Tree Species 3" species={treeSpecies3[regionName]} />
                <TreeSpeciesRow title="Tree Species 4" species={treeSpecies4[regionName]} />
                <TreeSpeciesRow title="Tree Species 5" species={treeSpecies5[regionName]} />
                <TreeSpeciesRow title="Tree Species 6" species={treeSpecies6[regionName]} />
              </>
            ))}
        </Table.Tbody>
      </Table>
    </Flex>
  );
}

export function Vegetation() {
  const { loading, error, constants } = useConstants('3.0.0');

  if (loading) {
    return (
      <PageWrapper>
        <Flex justify="center" align="center" h="calc(100vh - 120px - 32px)">
          <Loader size="lg" />
          <Text ml="md">Loading constants...</Text>
        </Flex>
      </PageWrapper>
    );
  }

  if (error || !constants) {
    return (
      <PageWrapper>
        <Flex direction="column" align="center" justify="center" h="calc(100vh - 120px - 32px)">
          <Text color="red" size="lg" mb="md">
            {error || 'Failed to load constants'}
          </Text>
          <Text size="sm" color="dimmed">
            Please try refreshing the page or contact support if the issue persists.
          </Text>
        </Flex>
      </PageWrapper>
    );
  }

  const categories = constants as DocCategory[];
  const commonConstants = categories.find((category) => category.name === 'Common');
  const treeRegions = commonConstants?.sections.find((section) => section.name === 'TREE_REGIONS');

  if (!commonConstants) {
    return (
      <PageWrapper>
        <Text color="red">Common constants not found</Text>
      </PageWrapper>
    );
  }

  if (!treeRegions) {
    return (
      <PageWrapper>
        <Text color="red">Tree regions not found</Text>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <VegetationDocs treeRegions={treeRegions} />
    </PageWrapper>
  );
}
